import { axiosAdminInstance } from '../utils/axios'
import { buildAgentLastMeetingInfo, clearEndedMeetingState, isForcedAgentInterrupt } from '../utils/meetingHelpers'
import { simd } from 'wasm-feature-detect'

const nlLocale = {
  dayNames: ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'],
  dayNamesShort: ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
  dayNamesMin: ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'],
  monthNames: [
    'jan', 'feb', 'mrt', 'apr', 'mei', 'jun',
    'jul', 'aug', 'sep', 'okt', 'nov', 'dec'
  ],
  monthNamesShort: [
    'jan', 'feb', 'mrt', 'apr', 'mei', 'jun',
    'jul', 'aug', 'sep', 'okt', 'nov', 'dec'
  ],
  weekHeader: 'Wk',
  firstDayOfWeek: 1, // Monday as the first day of the week (Dutch convention)
  dateFormat: 'dd-mm-yy' // Dutch commonly uses DD-MM-YYYY format
}
const enLocale = {
  dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  dayNamesMin: ['Sun', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat'],
  monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  weekHeader: 'Wk',
  firstDayOfWeek: 1,
  dateFormat: 'dd/mm/yy'
}

export default {
  data () {
    return {
      bell: null,
      emitStatus: true,
      emitDone: true,
      emitTimeout: null,
      isMobileDevice: window.DetectRTC.isMobileDevice,
      isIpad: navigator.userAgent.includes('Mac') && typeof navigator.maxTouchPoints !== 'undefined' && navigator.maxTouchPoints > 2,
      webrtcJoin: null,
      isCamSettingsOpen: false,
      heldCallRequest: null,
      isAppReconnecting: false,
      isAppDisconnected: false,
      isBeta: typeof process.env.VUE_APP_IS_BETA !== 'undefined' && process.env.VUE_APP_IS_BETA === 'true',
      nodeEnv: process.env.NODE_ENV,
      isWebsocketConnected: false,
      isWebsocketAuthenticated: false,
      segmentationModel: null,
      browserSIMDSupport: null,
      _notificationInterval: null
    }
  },
  methods: {
    setFullName (user) {
      if (user !== false) {
        if (typeof user.first_name !== 'undefined' && typeof user.last_name !== 'undefined') {
          return (user.first_name + ' ' + user.last_name).trim()
        } else if (typeof user.first_name !== 'undefined' && typeof user.last_name === 'undefined') {
          return user.first_name
        } else if (typeof user.first_name === 'undefined' && typeof user.last_name !== 'undefined') {
          return user.last_name
        } else if (typeof user.name !== 'undefined') {
          return user.name
        } else {
          return 'Agent'
        }
      } else {
        return 'Agent'
      }
    },
    setFirstName (user) {
      if (user !== false && typeof user.first_name !== 'undefined') {
        return user.first_name
      } else {
        return 'Agent'
      }
    },
    async getNotificationBar () {
      const notifications = await axiosAdminInstance.getNotifications()

      if (typeof notifications.status !== 'undefined' && notifications.status === 200 && typeof notifications.data !== 'undefined') {
        this.$store.commit('setNotificationBar', notifications.data)
      }
    }
  },
  computed: {
    getFeedbackForm () {
      const feedbackForm = this.$store.getters.getFeedbackForms.filter((form) => {
        if (this.$store.getters.getUser !== false) {
          return form.type === 'meetingFeedbackAgent'
        } else {
          return form.type === 'meetingFeedbackParticipant'
        }
      })

      if (process.env.NODE_ENV !== 'production') {
        console.log('Get feedback form from store:', feedbackForm.length === 0 ? null : feedbackForm[0].id)
      }

      return feedbackForm.length === 0 ? null : feedbackForm[0]
    }
  },
  async mounted () {
    console.log('Mounted app')
    try {
      const simdSupported = await simd()
      this.browserSIMDSupport = simdSupported
    } catch (e) {
      console.warn('SIMD detection failed:', e)
      this.browserSIMDSupport = false
    }

    this.$store.commit('setLocale')
    const lang = document.querySelector('html').getAttribute('lang')?.toLowerCase()
    if (this.$primevue?.config) {
      this.$primevue.config.locale = lang === 'nl' ? nlLocale : enLocale
    }
    // Stop all code below in mounted for button and form
    if (window.location.href.indexOf('/website_button') !== -1 || window.location.href.indexOf('/plannen') !== -1 || window.location.href.indexOf('/schedule') !== -1) {
      return true
    }

    this.bell = new Audio('/sounds/bell.mp3')
    this.bell.loop = false

    this.$store.commit('setNotificationBar', {})
    this.$store.commit('removeFeedbackForms')
    this.$store.commit('setMeetingStarted', false)
    // Preserve Direct/Meek agent interrupted-reload recovery only. A normal hangup's
    // meeting_ended must be cleared so personal participants can rejoin via token.
    if (!this.$store.getters.isRecoveringEndedMeeting) {
      clearEndedMeetingState(this.$store)
    }
    this.$store.commit('setMeetingDisconnected', false)
    this.$store.commit('setForcedLogout', false)

    this.$webrtc.stopLocalStreams()

    const appVariant = process.env.VUE_APP_PWA_NAME || 'wsd'
    console.log('App variant:', appVariant)
    // TODO: Remove after testing before deployment.
    if (appVariant === '123meekijken') {
      this.$store.commit('setProductMode', 'Meek')
      
      const isAndroidNativeWebView = (/\bwv\b/i.test(navigator.userAgent) || window.AndroidBridge) && /Android/i.test(navigator.userAgent)
      // The iOS 123meekijken app injects window.is123MeekijkenIOS at document start and
      // aliases window.AndroidBridge to its WKScriptMessageHandler, so the meeting-ended
      // bridge calls keep working unchanged. Treat it as a mobile WebView for layout.
      const isIOSNativeWebView = window.is123MeekijkenIOS === true
      const isAndroidWebView = isAndroidNativeWebView || isIOSNativeWebView
      this.$store.commit('setIsAndroidWebView', isAndroidWebView)

      document.documentElement.classList.add('meek-portal')
      if (isAndroidWebView) {
        document.documentElement.classList.add('android-webview')

        const updateViewportHeight = () => {
          document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`)
        }
        window.addEventListener('resize', updateViewportHeight)
        updateViewportHeight()
      }

      // Remove exising favicon
      const head = document.head
      const existingIcons = head.querySelectorAll("link[rel~='icon'], link[rel~='apple-touch-icon']")
      existingIcons.forEach(icon => head.removeChild(icon))

      // update favicon
      const favicon = '/img/icons/meek-favicon.ico?v=2'
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link')
      link.rel = 'icon'
      link.href = favicon
      document.getElementsByTagName('head')[0].appendChild(link)

      const appleLink = document.querySelector("link[rel~='apple-touch-icon']") || document.createElement('link')
      appleLink.rel = 'icon'
      appleLink.href = favicon
      document.getElementsByTagName('head')[0].appendChild(appleLink)

      document.title = '123meekijken'
    }

    if (window.location.href.indexOf('/meeting') === -1 || this.$store.getters.getProduct === 'personal') {
      this.$axios.meetingLogout()
    }

    if ((this.$store.getters.getUser === false && this.$store.getters.getAccessToken === null) || this.$store.getters.isUINavigationEnabled === false) {
      console.log('User is not found or UI is disabled, logout and disconnect WebRTC anyway')
      this.$axios.logout(false)
      this.$webrtc.disconnect(false)
    }

    if (this.$store.getters.getUser !== false) {
      if (window.location.href.indexOf('/meeting/') === -1) {
        const me = await this.$axios.getUser(true)

        if (me.status === 200) {
          this.$webrtc.connect()
        }
      }
    }

    this.$webrtc.EventBus.$on('onConnected', (event) => {
      this.isWebsocketConnected = true
    })

    this.$webrtc.EventBus.$on('onAuthenticated', (event) => {
      this.isWebsocketAuthenticated = true
    })

    this.$webrtc.EventBus.$on('onDeviceList', (event) => {
      console.log('Listing devices in main.js')

      this.$store.commit('setDeviceList', event.devices)
    })

    this.$webrtc.EventBus.$on('onRequestJoin', async (event) => {
      console.log('onRequestJoin event triggered')
      if (this.$store.getters.getProduct === 'personal') {
        if (this.$store.getters.getUser !== false) {
          let isMeetingHost = true
          if (event.action === 'invite' || event.action === 'colleague') {
            isMeetingHost = false
          }

          this.$store.commit('setIsMeetingHost', isMeetingHost)

          this.$eventBus.$emit('callAgent', event.action, event.requestedBy, event.room_id, event.account)
        }
      } else {
        if (this.$store.getters.getMeeting === false) {
          let betaQuery = ''
          if (this.isBeta) {
            betaQuery = '&beta=true'
          }

          const meetings = await this.$axios.get(this.$axios.routes.meeting + '?room_id=' + event.room_id + betaQuery)

          if (typeof meetings.data.result === 'undefined' || meetings.data.result === null) {
            return false
          }
          const meeting = meetings.data.result[0]

          this.$store.commit('setMeeting', meeting)
        }

        if (this.$route.name !== 'meeting') {
          this.webrtcJoin = {
            room_id: event.room_id,
            product: this.$store.getters.getMeeting.product,
            stream2way: this.$store.getters.getMeeting.stream_camera_two_way
          }

          await this.$router.push({ name: 'meeting' })
        } else {
          this.$webrtc.join(event.room_id, this.$store.getters.getMeeting.product, this.$store.getters.getMeeting.stream_camera_two_way, this.$store.getters.getUser !== false)
        }
      }
    })

    this.$webrtc.EventBus.$on('onEndCall', async (isForcedDisconnect) => {
      console.log('onEndCall event triggered')
      if (this.$store.getters.userHasFeature('weseedo_direct_recording') && this.$store.getters.getUser !== false) {
        // COULD-DO: add this to the config as well so that other user who need recording
        // for direct but do not want to upload to a remote server can do so
        this.$webrtc.setUploadBumicomRecording(true, this.$axios)
      }
      if (this.$store.getters.getMeetingEnded === false) {
        console.log('GET MEETING ENDED IS FALSE')
        this.$store.commit('setMeetingStarted', false)
        this.$store.commit('setMeetingEnded', true)

        if (this.$store.getters.getUser !== false) {
          this.$store.commit('setMeetingEndedAs', 'agent')
        } else {
          this.$store.commit('setMeetingEndedAs', 'participant')
        }

        const isInterruptedEnd = isForcedAgentInterrupt(this.$store.getters, isForcedDisconnect)
        this.$store.commit('setMeetingInterrupted', isInterruptedEnd)

        if (this.$store.getters.getMeeting !== false) {
          console.log('GET MEETING IS NOT EMPTY')
          this.$utils.postMessageToParent('MEETING_ENDED', { meeting_id: this.$store.getters.getMeeting.id })
          // function call for Android 123 App
          if (window.AndroidBridge) {
            // Make sure a recording in progress is fully saved to the device before the
            // native app tears the webview down.
            await this.$webrtc.flushPendingRecordingSave()
            window.AndroidBridge.onMeetingEnded()
          }
          let lastMeetingInfo
          if (this.$store.getters.getUser !== false) {
            lastMeetingInfo = buildAgentLastMeetingInfo(this.$store.getters.getMeeting, this.$store.getters.getUser)
          } else {
            lastMeetingInfo = {
              id: this.$store.getters.getMeeting.id,
              is_appointment: this.$store.getters.getMeeting.is_appointment
            }
            // Save company info for later use (e.g., file uploads after meeting ends)
            if (this.$store.getters.getMeeting.company) {
              lastMeetingInfo.company = {
                features: this.$store.getters.getMeeting.company.features || []
              }
            }
            lastMeetingInfo.participant = this.$store.getters.getMeeting.participant.id
            lastMeetingInfo.participantName = this.$store.getters.getMeeting.participant.name
          }

          this.$store.commit('setLastMeetingInfo', lastMeetingInfo)

          this.$axios.meetingLogout()
          this.$webrtc.stopLocalStreams()

          let newStatus
          if (this.$store.getters.getStatus === 'online') {
            newStatus = 'online'
          } else {
            newStatus = 'offline'
          }

          if (this.$store.getters.getUser === false || this.$store.getters.isUINavigationEnabled === false || this.$store.getters.isMeetingLinkSession === true) {
            console.log('User is not found, UI is disabled, or meeting-link session ended; disconnect WebRTC')
            this.$webrtc.disconnect(false)
          } else {
            console.log('RESET THE STATUS TO', newStatus)
            setTimeout(() => {
              this.$webrtc.setStatus(this.$store.getters.isUINavigationEnabled ? newStatus : 'offline', true, false, false)
            }, 1000)
          }
        }
      }
    })

    this.$webrtc.EventBus.$on('onLostConnection', () => {
      console.log('onLostConnection event triggered')
      this.isAppReconnecting = true
    })

    // The room never connected, so the call ends and an agent is sent back to the waiting
    // room. Toast rather than a modal: this is handled app-level, so it outlives that
    // navigation and the component teardown that goes with it.
    this.$webrtc.EventBus.$on('onRoomConnectFailed', () => {
      console.log('onRoomConnectFailed event triggered')
      this.$toast.add({
        severity: 'error',
        summary: this.$t('MeetingRoom.Connection failed'),
        detail: this.$t('MeetingRoom.Could not establish the video connection, please check your network and try again'),
        life: 8000
      })
    })

    /*
    this.$webrtc.EventBus.$on('onReconnectAttempt', (attempt) => {
      console.log('onReconnectAttempt event triggered')
      this.isAppReconnecting = true
      this.reconnectAttempt = attempt.attempt
    })
     */

    this.$webrtc.EventBus.$on('onReconnected', () => {
      console.log('onReconnected event triggered')
      this.isAppReconnecting = false
    })

    this.$webrtc.EventBus.$on('onDisconnected', async (event) => {
      console.log('onDisconnected event triggered')

      this.isAppReconnecting = false

      if (['logout', 'forced_logout'].includes(this.$route.name) === false && this.$store.getters.getMeetingEnded === false && this.$store.getters.getMeetingDisconnected === false && this.$store.getters.getForcedLogout === false) {
        if (this.$store.getters.getUser !== false) {
          this.$store.commit('setMeetingDisconnectedAs', 'agent')
        } else {
          this.$store.commit('setMeetingDisconnectedAs', 'participant')
        }
        this.$store.commit('setMeetingDisconnected', true)

        if (this.$store.getters.getMeeting !== false) {
          this.$axios.meetingLogout()

          // Notify Android 123 App that the meeting has ended due to disconnect
          if (window.AndroidBridge) {
            // Flush a recording in progress to the device before the webview is torn down.
            await this.$webrtc.flushPendingRecordingSave()
            window.AndroidBridge.onMeetingEnded()
          }
        }

        if (this.$store.getters.getUser !== false) {
          this.$axios.logout(false)
        }

        this.$webrtc.stopLocalStreams()

        console.log('Set isAppDisconnected')
        this.isAppDisconnected = true
      }
    })

    this.$webrtc.EventBus.$on('onForcedLogout', (event) => {
      if (this.$store.getters.getForcedLogout === false) {
        this.$store.commit('setForcedLogout', true)

        this.$router.push({ name: 'forced_logout' })
      }
    })

    this.$webrtc.EventBus.$on('onVisitorOnline', (username, action, agentId) => {
      if (this.$store.getters.getUser !== false) {
        if (this.$store.getters.userHasFeature('waitingroom') && action === 'join-room' && agentId === this.$store.getters.getUser.id && this.$store.getters.getMeeting === false && (typeof this.$route.query.disable_ringtone_waiting_room === 'undefined' || this.$route.query.disable_ringtone_waiting_room === 'false')) {
          this.bell.play()
        }
      }
    })

    this.$jQuery(window).on('resize', () => {
      setTimeout(() => {
        if (typeof window.innerWidth !== 'undefined' && typeof window.innerHeight !== 'undefined') {
          if (window.innerWidth > window.innerHeight) {
            this.$eventBus.$emit('changeWindowOrientation', 'landscape')
          } else if (window.innerWidth < window.innerHeight) {
            this.$eventBus.$emit('changeWindowOrientation', 'portrait')
          } else {
            // when pixels are the same wait for one more time on resize
            this.$jQuery(window).one('resize', () => {
              if (window.innerWidth > window.innerHeight) {
                this.$eventBus.$emit('changeWindowOrientation', 'landscape')
              } else if (window.innerWidth < window.innerHeight) {
                this.$eventBus.$emit('changeWindowOrientation', 'portrait')
              }
            })
          }
        } else {
          // No inner Width and Height found, but orientation still changed. Just commit with orientation null
          this.$eventBus.$emit('changeWindowOrientation', null)
        }
      }, 250)
    })

    /*
    if (this.isMobileDevice) {
      this.$jQuery(window).on('resize', () => {
        if (typeof this.$route.name !== 'undefined' && this.$route.name !== 'meeting') {
          const footer = this.$jQuery('#footerContainer').outerHeight()
          if (this.$jQuery('body').find('.h-100-generated').length === 0) {
            this.$jQuery('body').find('.h-100').first().addClass('h-100-generated').removeClass('h-100')
          }
          this.$jQuery('body').find('.h-100-generated').css({ 'height': window.innerHeight - footer + 'px' })
        }
      })
    }
     */

    this._notificationInterval = setInterval(() => {
      if (window.location.href.indexOf('/meeting/') === -1) {
        this.getNotificationBar()
      }
    }, 30000)
  },
  beforeUnmount () {
    // Clear notification interval
    if (this._notificationInterval) {
      clearInterval(this._notificationInterval)
      this._notificationInterval = null
    }

    // Remove all WebRTC EventBus listeners
    this.$webrtc.EventBus.$off('onConnected')
    this.$webrtc.EventBus.$off('onAuthenticated')
    this.$webrtc.EventBus.$off('onDeviceList')
    this.$webrtc.EventBus.$off('onRequestJoin')
    this.$webrtc.EventBus.$off('onEndCall')
    this.$webrtc.EventBus.$off('onLostConnection')
    this.$webrtc.EventBus.$off('onRoomConnectFailed')
    this.$webrtc.EventBus.$off('onReconnected')
    this.$webrtc.EventBus.$off('onDisconnected')
    this.$webrtc.EventBus.$off('onForcedLogout')
    this.$webrtc.EventBus.$off('onVisitorOnline')

    // Remove window resize listener
    this.$jQuery(window).off('resize')
  }
}
